import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Link, 
  useMediaQuery, 
  useTheme, 
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, logout, schedulingEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <Box component="header" 
         sx={{ 
           borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
           backgroundColor: '#ffffff',
           boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
           width: '100%'
         }}>
      <Container maxWidth="lg" sx={{ py: { xs: 0.75, sm: 2 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}>
          {/* Logo - Far Left */}
          <Link 
            onClick={() => isAuthenticated ? navigate('/jobs') : navigate('/login')} 
            underline="none" 
            sx={{ display: 'flex', cursor: 'pointer', flexShrink: 0, mr: 4 }}
          >
            <Box component="img" 
                 src="/magna-logo-official-dark.svg" 
                 alt="Magna Legal Services" 
                 sx={{ 
                   height: { xs: 28, sm: 40 }, 
                   maxWidth: { xs: '130px', sm: '180px' } 
                 }} />
          </Link>

          {/* Title - Center */}
          {!isMobile && (
            <Typography 
              variant="h6" 
              color="primary" 
              component="div"
              sx={{
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
            >
              Reporter Portal
            </Typography>
          )}

          {/* Navigation and Buttons - Right */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row',
              alignItems: 'center', 
              gap: 2,
              flexShrink: 0
            }}>
              {/* Navigation Links */}
              {isAuthenticated && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    color={isActive('/home') ? "primary" : "inherit"}
                    onClick={() => navigate('/home')}
                    sx={{ 
                      fontWeight: isActive('/home') ? 'bold' : 'normal',
                      textDecoration: isActive('/home') ? 'underline' : 'none',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Dashboard
                  </Button>
                  <Button 
                    color={isActive('/jobs') ? "primary" : "inherit"}
                    onClick={() => navigate('/jobs')}
                    sx={{ 
                      fontWeight: isActive('/jobs') ? 'bold' : 'normal',
                      textDecoration: isActive('/jobs') ? 'underline' : 'none',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Jobs
                  </Button>
                  {schedulingEnabled && (
                    <Button 
                      color={isActive('/schedule') ? "primary" : "inherit"}
                      onClick={() => navigate('/schedule')}
                      sx={{ 
                        fontWeight: isActive('/schedule') ? 'bold' : 'normal',
                        textDecoration: isActive('/schedule') ? 'underline' : 'none',
                        minWidth: 'auto',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Schedule
                    </Button>
                  )}
                  <Button 
                    color={isActive('/account-settings') ? "primary" : "inherit"}
                    onClick={() => navigate('/account-settings')}
                    sx={{ 
                      fontWeight: isActive('/account-settings') ? 'bold' : 'normal',
                      textDecoration: isActive('/account-settings') ? 'underline' : 'none',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Settings
                  </Button>
                </Box>
              )}
              
              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {isAuthenticated && (
                  <Box 
                    component="button" 
                    onClick={logout}
                    className="btn btn-secondary"
                    sx={{ 
                      fontSize: { sm: '0.75rem', md: '0.875rem' },
                      py: { sm: 1, md: 1.5 },
                      px: { sm: 2, md: 3 },
                      minWidth: { sm: 'auto', md: '120px' },
                      color: '#ED7420',
                      backgroundColor: 'transparent',
                      border: '1px solid #ED7420',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#fff !important',
                        backgroundColor: '#ff6a00 !important',
                        border: '1px solid #ff6a00 !important'
                      },
                      touchAction: 'manipulation',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}>
                    Logout
                  </Box>
                )}
                <Link href="https://magnals.com/contact-magna-legal-services/" underline="none" target="_blank">
                  <Box component="button" 
                       className="btn btn-secondary"
                       sx={{ 
                         fontSize: { sm: '0.75rem', md: '0.875rem' },
                         py: { sm: 1, md: 1.5 },
                         px: { sm: 2, md: 3 },
                         minWidth: { sm: 'auto', md: '120px' },
                         color: '#ED7420',
                         backgroundColor: 'transparent',
                         border: '1px solid #ED7420',
                         transition: 'all 0.3s ease',
                         '&:hover': {
                           color: '#fff !important',
                           backgroundColor: '#ff6a00 !important',
                           border: '1px solid #ff6a00 !important'
                         },
                         touchAction: 'manipulation',
                         display: 'inline-flex',
                         justifyContent: 'center',
                         alignItems: 'center',
                         whiteSpace: 'nowrap'
                       }}>
                    Contact Us
                  </Box>
                </Link>
                {(location.pathname === '/login' || location.pathname.startsWith('/worksheet/')) && (
                  <Box 
                    component="button" 
                    onClick={() => navigate('/help')}
                    className="btn btn-secondary"
                    sx={{ 
                      fontSize: { sm: '0.75rem', md: '0.875rem' },
                      py: { sm: 1, md: 1.5 },
                      px: { sm: 2, md: 3 },
                      minWidth: { sm: 'auto', md: '80px' },
                      color: '#ED7420',
                      backgroundColor: 'transparent',
                      border: '1px solid #ED7420',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#fff !important',
                        backgroundColor: '#ff6a00 !important',
                        border: '1px solid #ff6a00 !important'
                      },
                      touchAction: 'manipulation',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}>
                    Help
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="end"
              onClick={handleMobileMenuToggle}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Container>
      
      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
            Reporter Portal
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        
        {isAuthenticated && (
          <>
            <List>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleNavigate('/home')}
                  selected={isActive('/home')}
                >
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleNavigate('/jobs')}
                  selected={isActive('/jobs')}
                >
                  <ListItemText primary="Jobs" />
                </ListItemButton>
              </ListItem>
              {schedulingEnabled && (
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleNavigate('/schedule')}
                    selected={isActive('/schedule')}
                  >
                    <ListItemText primary="Schedule" />
                  </ListItemButton>
                </ListItem>
              )}
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleNavigate('/account-settings')}
                  selected={isActive('/account-settings')}
                >
                  <ListItemText primary="Settings" />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider />
          </>
        )}
        
        <List>
          {isAuthenticated && (
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton 
              component="a"
              href="https://magnals.com/contact-magna-legal-services/"
              target="_blank"
            >
              <ListItemText primary="Contact Us" />
            </ListItemButton>
          </ListItem>
          {(location.pathname === '/login' || location.pathname.startsWith('/worksheet/')) && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigate('/help')}>
                <ListItemText primary="Help" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default Header;